import { A } from "@solidjs/router";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbAlertTriangle, TbChevronDown, TbMessage2, TbTag, TbX } from "solid-icons/tb";
import { type Component, Index, Show } from "solid-js";
import { AssetTryAgainButton } from "~/components/assets/AssetTryAgainButton";
import { AssetLifecycleStatusGlyph } from "~/components/utils/AssetLifecycleStatusGlyph";
import {
  assetLifecycleUserStatusI18nKey,
  assetTerminalErrorKind,
  assetTerminalErrorLabelKey,
} from "~/components/utils/lifecycleStateMappers";
import { SUPPORT_MAILTO } from "~/constants/urls";
import { useI18n } from "~/i18n/context";
import type { TranslationKey } from "~/i18n/translations";
import { resolveClientUploadLifecycleState } from "~/lib/file-upload/resolveClientUploadLifecycleState";
import type { FileUploadState } from "~/lib/file-upload/useFileUpload";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

interface UploadProgressModalProps {
  uploads: () => FileUploadState[];
  /** Whether the modal is minimized (pill only) */
  minimized: boolean;
  /** True when every upload in the batch is completed. Switches the pill to a success indicator. */
  allReady?: boolean;
  onMinimize: () => void;
  onClose: () => void;
  /** Build URL for viewing the asset (project assets page with asset in side). When provided, "View" is shown for completed uploads with assetId. */
  getViewUrl?: (projectId: string, assetId: string) => string;
  /** Called when user clicks "Chat" for one asset. Parent should add asset to scope and navigate to new chat (same as asset page Chat button). */
  onChatWithAsset?: (projectId: string, assetId: string) => void;
  /** Called when user clicks "Chat with n files". Parent should add all assets to scope and navigate to new chat (same as assets list Chat button). */
  onChatWithFiles?: (projectId: string, assetIds: string[]) => void;
  /** Called when user clicks "Label Files" – receives projectId and assetIds of completed uploads. Parent should navigate to assets and open bulk label picker. */
  onLabelFiles?: (projectId: string, assetIds: string[]) => void;
}

function rowAriaLabel(
  upload: FileUploadState,
  resolvedState: string,
  t: (key: TranslationKey, params?: Record<string, string | number>) => string
): string {
  if (upload.status === "error") {
    return upload.error ? `${t("common.error")}: ${upload.error}` : t("common.error");
  }
  const terminalKey = assetTerminalErrorLabelKey(assetTerminalErrorKind(resolvedState));
  if (terminalKey) return t(terminalKey);
  if (upload.status === "completed") return t("asset_lifecycle_ready");
  const key = assetLifecycleUserStatusI18nKey(resolvedState);
  return key ? t(key) : t("asset_lifecycle_processing");
}

const UploadProgressModalRow: Component<{
  upload: FileUploadState;
  getViewUrl?: (projectId: string, assetId: string) => string;
  onChatWithAsset?: (projectId: string, assetId: string) => void;
}> = (props) => {
  const { t } = useI18n();
  const wire = useWire();

  const resolvedState = () => resolveClientUploadLifecycleState(props.upload, (id) => wire.assets.get(id));
  const terminalKind = () => assetTerminalErrorKind(resolvedState());
  const asset = () => (props.upload.assetId ? wire.assets.get(props.upload.assetId) : undefined);

  const isAssetTerminalError = () => terminalKind() !== null;
  const isInsufficientCredits = () => terminalKind() === "insufficient_credits";

  const viewUrl = () => {
    const assetId = props.upload.assetId;
    return assetId && props.getViewUrl ? props.getViewUrl(props.upload.projectId, assetId) : undefined;
  };
  const chatWithAsset = () => {
    const assetId = props.upload.assetId;
    const onChatWithAsset = props.onChatWithAsset;
    return assetId && onChatWithAsset ? () => onChatWithAsset(props.upload.projectId, assetId) : undefined;
  };

  const statusText = () => {
    if (props.upload.status === "error") return t("common.error");
    const terminalKey = assetTerminalErrorLabelKey(terminalKind());
    if (terminalKey) return t(terminalKey);
    if (props.upload.status === "completed") return t("asset_lifecycle_ready");
    const key = assetLifecycleUserStatusI18nKey(resolvedState());
    return key ? t(key) : t("asset_lifecycle_processing");
  };

  const statusTextClass = () => {
    if (props.upload.status === "error" || isAssetTerminalError()) return "text-red-600 dark:text-red-400";
    if (props.upload.status === "completed") return "text-success";
    if (props.upload.status === "pending") return "text-secondary-text";
    if (resolvedState() === "INDEXING") return "text-amber-600 dark:text-amber-400";
    return "text-brand";
  };

  const statusTooltipContent = () => {
    if (isInsufficientCredits()) {
      return (
        <span class="block max-w-[min(20rem,calc(100vw-2rem))] text-left wrap-break-word">
          <span class="block">{t("asset_insufficient_credits")}</span>
          <span class="mt-1 block">
            {t("asset_please_top_up_credits")}{" "}
            <A class="text-brand underline" href="/upgrade">
              {t("asset_top_up_your_credits")}
            </A>{" "}
            {t("asset_and_try_again")}
          </span>
        </span>
      );
    }
    return (
      <span class="block max-w-[min(20rem,calc(100vw-2rem))] text-left wrap-break-word">
        {rowAriaLabel(props.upload, resolvedState(), t)}
      </span>
    );
  };

  return (
    <li class="flex min-w-0 flex-col gap-1 rounded-lg border border-primary-border/60 bg-canvas/50 px-3 py-2 text-sm">
      <div class="flex min-w-0 flex-nowrap items-center gap-2">
        <StTooltip
          placement="top"
          content={
            <span class="block max-w-[min(24rem,calc(100vw-2rem))] text-left text-sm wrap-break-word">
              {props.upload.file.name}
            </span>
          }
        >
          <StTooltipTrigger
            as="span"
            class="min-w-[15ch] flex-1 cursor-default truncate bg-transparent! text-primary-text data-[state=open]:bg-transparent!"
            aria-label={props.upload.file.name}
          >
            {props.upload.file.name}
          </StTooltipTrigger>
        </StTooltip>
        <div class="min-w-0 shrink-2 overflow-hidden">
          <StTooltip placement="top" content={statusTooltipContent()}>
            <StTooltipTrigger
              as="span"
              class={`flex max-w-full min-w-0 cursor-default items-center gap-1.5 overflow-hidden bg-transparent! text-xs font-medium data-[state=open]:bg-transparent! ${statusTextClass()}`}
              aria-label={rowAriaLabel(props.upload, resolvedState(), t)}
            >
              <Show when={props.upload.status === "error" || isAssetTerminalError()}>
                <TbAlertTriangle class="size-3.5 shrink-0" aria-hidden="true" />
              </Show>
              <Show when={props.upload.status === "completed" && !isAssetTerminalError()}>
                <span class="shrink-0">
                  <AssetLifecycleStatusGlyph state="READY" sizeClass="size-4" />
                </span>
              </Show>
              <Show
                when={props.upload.status !== "completed" && props.upload.status !== "error" && !isAssetTerminalError()}
              >
                <span class="shrink-0">
                  <AssetLifecycleStatusGlyph
                    state={resolvedState()}
                    assumeSpinnerWhenUnmapped
                    sizeClass="size-3.5"
                    spinnerClass="text-brand"
                  />
                </span>
              </Show>
              <span class="min-w-0 flex-1 truncate">{statusText()}</span>
            </StTooltipTrigger>
          </StTooltip>
        </div>
        <div class="flex shrink-0 flex-nowrap items-center gap-1.5">
          <Show
            when={viewUrl()}
            fallback={
              <span
                class="cursor-not-allowed rounded border border-primary-border bg-panel px-2 py-1 text-xs font-medium whitespace-nowrap text-muted-text opacity-60 select-none"
                aria-disabled="true"
              >
                {t("view")}
              </span>
            }
          >
            {(href) => (
              <A
                href={href()}
                class="rounded border border-primary-border bg-panel px-2 py-1 text-xs font-medium whitespace-nowrap text-primary-text hover:bg-panel-hover focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
              >
                {t("view")}
              </A>
            )}
          </Show>
          <Show
            when={isAssetTerminalError() && asset()}
            fallback={
              <Show
                when={chatWithAsset()}
                fallback={
                  <span
                    class="inline-flex shrink-0 cursor-not-allowed items-center gap-1.5 rounded border border-transparent bg-brand px-2 py-1 text-xs font-medium text-on-brand opacity-60 select-none"
                    aria-disabled="true"
                  >
                    <TbMessage2 class="size-3.5 shrink-0" aria-hidden="true" />
                    {t("button_chat")}
                  </span>
                }
              >
                {(chat) => (
                  <button
                    type="button"
                    onClick={() => chat()()}
                    class="inline-flex shrink-0 items-center gap-1.5 rounded border border-transparent bg-brand px-2 py-1 text-xs font-medium whitespace-nowrap text-on-brand hover:bg-brand-hover focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
                  >
                    <TbMessage2 class="size-3.5 shrink-0" aria-hidden="true" />
                    {t("button_chat")}
                  </button>
                )}
              </Show>
            }
          >
            {(terminalAsset) => <AssetTryAgainButton asset={terminalAsset()} />}
          </Show>
        </div>
      </div>
      <Show when={props.upload.status === "error"}>
        <div class="flex min-w-0 flex-col gap-1">
          <Show when={props.upload.error}>
            <p class="min-w-0 text-xs wrap-break-word text-red-600 dark:text-red-400" title={props.upload.error}>
              {props.upload.error}
            </p>
          </Show>
          <p class="min-w-0 text-xs text-secondary-text">
            {t("upload_error_try_again_or")}
            <a
              href={SUPPORT_MAILTO}
              class="rounded underline hover:text-primary-text focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
              onClick={() => trackFeature({ feature: "nav.help", via: "button" })}
            >
              {t("upload_error_get_support")}
            </a>
          </p>
        </div>
      </Show>
    </li>
  );
};

/**
 * Modal that slides up from the bottom-right showing file upload progress.
 * Lists each file with status (Uploading, Processing, Ready). User can minimize to a pill or close (X).
 */
export const UploadProgressModal: Component<UploadProgressModalProps> = (props) => {
  const { t } = useI18n();

  const activeCount = () => props.uploads().filter((u) => u.status === "uploading" || u.status === "processing").length;
  const totalCount = () => props.uploads().length;

  /** Uploads that have an assetId (processing or completed) – safe to View, Chat, or Label. */
  const uploadsWithAssetId = () =>
    props.uploads().filter((u) => (u.status === "processing" || u.status === "completed") && u.assetId) as Array<
      FileUploadState & { assetId: string }
    >;
  const canLabelFiles = () => uploadsWithAssetId().length > 0 && props.onLabelFiles;
  const handleLabelFiles = () => {
    const list = uploadsWithAssetId();
    if (list.length === 0 || !list[0].projectId || !props.onLabelFiles) return;
    props.onLabelFiles(
      list[0].projectId,
      list.map((u) => u.assetId)
    );
  };

  return (
    <>
      <Show when={props.minimized && totalCount() > 0}>
        <button
          type="button"
          onClick={props.onMinimize}
          class="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center gap-2 rounded-full border border-primary-border bg-panel px-4 py-2.5 text-sm font-medium text-primary-text shadow-lg transition-all hover:bg-panel-hover focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-canvas focus:outline-none sm:left-auto sm:mx-0 sm:max-w-none"
          aria-label={props.allReady ? t("upload_modal_all_ready") : t("upload_modal_title")}
        >
          <Show when={props.allReady} fallback={<span class="size-2 shrink-0 animate-pulse rounded-full bg-brand" />}>
            <span class="shrink-0">
              <AssetLifecycleStatusGlyph state="READY" sizeClass="size-4" />
            </span>
          </Show>
          <Show
            when={props.allReady}
            fallback={
              <span>
                {t("upload_modal_title")} ({activeCount()}/{totalCount()})
              </span>
            }
          >
            <span>{t("upload_modal_all_ready")}</span>
          </Show>
          <TbChevronDown class="size-4 shrink-0 rotate-180" aria-hidden="true" />
        </button>
      </Show>

      <Show when={!props.minimized && totalCount() > 0}>
        <div
          class="fixed inset-x-4 bottom-4 z-50 w-auto max-w-none overflow-hidden rounded-xl border border-primary-border bg-panel shadow-xl transition-[transform,opacity] duration-300 ease-out sm:right-4 sm:left-auto sm:w-[min(42rem,calc(100vw-2rem))]"
          role="dialog"
          aria-labelledby="upload-modal-title"
          aria-label={t("upload_modal_title")}
        >
          <div class="flex items-center justify-between gap-2 border-b border-primary-border bg-panel px-4 py-3">
            <div class="min-w-0 flex-1">
              <h2 id="upload-modal-title" class="text-sm font-semibold text-primary-text">
                <Show when={props.allReady} fallback={t("upload_modal_title")}>
                  {t("upload_modal_all_ready")}
                </Show>
              </h2>
              <p class="mt-0.5 text-xs text-secondary-text">{t("upload_modal_can_close_hint")}</p>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={props.onMinimize}
                class="rounded p-1.5 text-secondary-text hover:bg-panel-hover hover:text-primary-text focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
                aria-label={t("upload_modal_minimize")}
              >
                <TbChevronDown class="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={props.onClose}
                class="rounded p-1.5 text-secondary-text hover:bg-panel-hover hover:text-primary-text focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
                aria-label={t("common.close")}
              >
                <TbX class="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <Show when={uploadsWithAssetId().length > 0}>
            <div class="flex justify-end gap-2 border-b border-primary-border bg-panel px-4 py-2">
              <Show when={canLabelFiles()}>
                <button
                  type="button"
                  onClick={handleLabelFiles}
                  class="inline-flex shrink-0 items-center gap-1.5 rounded border border-primary-border bg-panel px-2 py-1.5 text-xs font-medium whitespace-nowrap text-primary-text hover:bg-panel-hover focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
                >
                  <TbTag class="size-3.5" aria-hidden="true" />
                  {uploadsWithAssetId().length === 1 ? t("upload_modal_label_file") : t("upload_modal_label_files")}
                </button>
              </Show>
              <Show when={props.onChatWithFiles && uploadsWithAssetId().length > 1}>
                <button
                  type="button"
                  onClick={() => {
                    const list = uploadsWithAssetId();
                    if (list.length === 0 || !list[0].projectId || !props.onChatWithFiles) return;
                    props.onChatWithFiles(
                      list[0].projectId,
                      list.map((u) => u.assetId)
                    );
                  }}
                  class="inline-flex items-center gap-1.5 rounded border border-transparent bg-brand px-2 py-1.5 text-xs font-medium whitespace-nowrap text-on-brand hover:bg-brand-hover focus:inset-ring-2 focus:inset-ring-brand focus:outline-none"
                >
                  <TbMessage2 class="size-3.5 shrink-0" aria-hidden="true" />
                  {t("upload_modal_chat_with_n_files", { count: uploadsWithAssetId().length })}
                </button>
              </Show>
            </div>
          </Show>

          <div class="max-h-64 overflow-y-auto p-2">
            <ul class="space-y-1.5">
              {/* Keyed by position: every status change replaces the upload object, so keying by
                  reference would tear down and rebuild the row. */}
              <Index each={props.uploads()}>
                {(upload) => (
                  <UploadProgressModalRow
                    upload={upload()}
                    getViewUrl={props.getViewUrl}
                    onChatWithAsset={props.onChatWithAsset}
                  />
                )}
              </Index>
            </ul>
          </div>
        </div>
      </Show>
    </>
  );
};
